import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRebaseClient } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    TextField as RebaseTextField,
    Typography as RebaseTypography,
    cls,
    defaultBorderMixin
} from "@rebasepro/ui";

import { plural } from "../plural";
import {
    NOTE_MAX,
    PRESENTATION_STATE_COLOR,
    PRESENTATION_STATE_HINT,
    PRESENTATION_STATE_LABEL,
    noteProblem,
    presentationAnswerLine,
    presentationState,
    type PresentationState
} from "../presentations";
import { EntityLink, entityIdOf } from "./_links";
import { sinceLabel } from "./_prospects_shared";
import { CandidateShortlist, shortlistLabel, type ShortlistCandidate } from "./CandidateShortlist";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const TextField = RebaseTextField as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;

/**
 * Presenting candidates to an employer, from the team's side (drizzle/0069).
 *
 * One module behind three doors — the company's cover, the candidate's cover,
 * and the shortlist in the outreach composer — because the part that must not
 * vary is the part a second copy would get wrong: what the confirmation says
 * the employer will be able to read, and that nothing is written until a person
 * presses the button with a note for every name.
 *
 * ── This is a person choosing, never a system proposing ─────────────
 *
 * Nothing here suggests a candidate for a company or a company for a candidate.
 * The candidates come from a search somebody typed or from the record somebody
 * opened; the company comes from the record somebody opened or from a list of
 * every employer with an account, in alphabetical order. PRODUCT.md allows a
 * human naming candidates to an employer and forbids a system inferring the
 * link — this file is the first half and nothing of the second.
 */

export interface PresentationRow {
    id: string;
    company_id?: unknown;
    talent_id?: unknown;
    job_id?: unknown;
    note_for_employer?: string | null;
    presented_by?: string | null;
    presented_at?: string | null;
    employer_state?: string | null;
    employer_state_at?: string | null;
    decline_reason?: string | null;
}

interface CompanyContext {
    members: { id: string; contact_name?: string | null; contact_email?: string | null }[];
    jobs: { id: string; title?: string | null; status?: string | null }[];
}

/**
 * The presentations for one company or one candidate, with every id they carry
 * resolved to a name.
 *
 * `rows === null` with `failed === false` is "still reading"; with `failed` it
 * is "could not read". Neither renders as "nobody has been presented" — the
 * sentence a recruiter would act on by presenting somebody twice.
 */
export function usePresentations(scope: { company_id?: string | null; talent_id?: string | null }) {
    const client = useRebaseClient<RebaseClient>();
    const data = (client as any)?.data;
    const key = scope.company_id ? `company:${scope.company_id}` : scope.talent_id ? `talent:${scope.talent_id}` : null;

    const [rows, setRows] = useState<PresentationRow[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [names, setNames] = useState({
        talent: new Map<string, string>(),
        company: new Map<string, string>(),
        job: new Map<string, string>(),
        presenter: new Map<string, string>()
    });
    const wanted = useRef<string | null>(null);

    useEffect(() => { setRows(null); setFailed(false); }, [key]);

    const reload = useCallback(async () => {
        if (!data || !key) return;
        wanted.current = key;
        const where = scope.company_id
            ? { company_id: ["==", scope.company_id] }
            : { talent_id: ["==", scope.talent_id] };
        let found: PresentationRow[];
        try {
            const page = await data.collection("presented-candidates").find({
                where,
                orderBy: ["presented_at", "desc"],
                limit: 200
            });
            found = (page?.data ?? []) as PresentationRow[];
        } catch {
            if (wanted.current !== key) return;
            setRows(null);
            setFailed(true);
            return;
        }
        if (wanted.current !== key) return;
        setRows(found);
        setFailed(false);

        // Names cost labels if they fail, never the list.
        const ids = (field: keyof PresentationRow) =>
            [...new Set(found.map(r => entityIdOf(r[field])).filter((v): v is string => Boolean(v)))];
        const lookup = async (collection: string, list: string[], label: (row: any) => string) => {
            const out = new Map<string, string>();
            if (list.length === 0) return out;
            try {
                const page = await data.collection(collection).find({ where: { id: ["in", list] }, limit: list.length });
                for (const row of (page?.data ?? []) as any[]) {
                    if (row?.id) out.set(String(row.id), label(row));
                }
            } catch { /* named "(sin nombre)" below */ }
            return out;
        };
        const [talent, company, job, presenter] = await Promise.all([
            lookup("talents", ids("talent_id"), r => (r.full_name ?? "").trim() || "(sin nombre)"),
            lookup("companies", ids("company_id"), r => (r.name ?? "").trim() || "(empresa sin nombre)"),
            lookup("jobs", ids("job_id"), r => (r.title ?? "").trim() || "(vacante sin título)"),
            lookup("users", [...new Set(found.map(r => r.presented_by).filter((v): v is string => Boolean(v)))],
                r => ((r.displayName ?? "").trim() || String(r.email ?? "")).split(/[\s@]/)[0] || "alguien del equipo")
        ]);
        if (wanted.current !== key) return;
        setNames({ talent, company, job, presenter });
    }, [data, key, scope.company_id, scope.talent_id]);

    useEffect(() => { void reload(); }, [reload]);

    return { rows, failed, names, reload };
}

/** "3 presentados · 1 contactado · 1 descartado por la empresa" — the loop, in one line. */
export function presentationTally(rows: PresentationRow[]): string {
    const count = (state: PresentationState) => rows.filter(r => presentationState(r.employer_state) === state).length;
    const contacted = count("contacted");
    const declined = count("declined");
    const untouched = count("new");
    return [
        `${rows.length} ${plural(rows.length, "presentado", "presentados")}`,
        contacted ? `${contacted} ${plural(contacted, "contactado", "contactados")}` : null,
        declined ? `${declined} ${plural(declined, "descartado", "descartados")} por la empresa` : null,
        untouched ? `${untouched} sin abrir` : null
    ].filter(Boolean).join(" · ");
}

/**
 * One presentation as the team reads it: who (or where), what we said, what the
 * employer did. `perspective` decides which end of the pair is the title — the
 * candidate on a company's cover, the company on a candidate's.
 */
export function PresentationCard({
    row,
    perspective,
    names,
    onWithdrawn
}: {
    row: PresentationRow;
    perspective: "company" | "talent";
    names: ReturnType<typeof usePresentations>["names"];
    onWithdrawn: () => void;
}) {
    const client = useRebaseClient<RebaseClient>();
    const data = (client as any)?.data;
    const [confirming, setConfirming] = useState(false);
    const [working, setWorking] = useState(false);
    const [error, setError] = useState("");

    const state = presentationState(row.employer_state);
    const talentId = entityIdOf(row.talent_id);
    const companyId = entityIdOf(row.company_id);
    const jobId = entityIdOf(row.job_id);
    const talentName = (talentId && names.talent.get(talentId)) || "(sin nombre)";
    const companyName = (companyId && names.company.get(companyId)) || "(empresa sin nombre)";
    const presenter = row.presented_by ? names.presenter.get(row.presented_by) : null;

    const withdraw = async () => {
        if (!data) return;
        setWorking(true);
        setError("");
        try {
            await data.collection("presented-candidates").delete(row.id);
            onWithdrawn();
        } catch (err: any) {
            setError(err?.message || "No se pudo retirar la presentación");
            setWorking(false);
        }
    };

    return (
        <Card className="p-3 flex flex-col gap-2">
            <div className="flex items-start gap-2 flex-wrap">
                <Typography variant="body2" className="grow font-medium">
                    {perspective === "company"
                        ? <EntityLink kind="talent" id={talentId}>{talentName}</EntityLink>
                        : <EntityLink kind="company" id={companyId}>{companyName}</EntityLink>}
                </Typography>
                <Chip size="small" colorScheme={PRESENTATION_STATE_COLOR[state]}>
                    {PRESENTATION_STATE_LABEL[state]}
                </Chip>
            </div>
            <Typography variant="body2" color="secondary">
                {[
                    jobId
                        ? <span key="job">para <EntityLink kind="job" id={jobId}>{names.job.get(jobId) ?? "una vacante"}</EntityLink></span>
                        : "presentación general, sin vacante concreta",
                    `presentado ${sinceLabel(row.presented_at)}${presenter ? ` por ${presenter}` : ""}`
                ].map((part, i) => <React.Fragment key={i}>{i > 0 ? " · " : ""}{part}</React.Fragment>)}
            </Typography>
            {/* The employer's answer is the feedback loop, so the reason is
                quoted in full rather than behind a hover. */}
            <Typography variant="body2" className={state === "declined" ? "font-medium" : undefined}>
                {presentationAnswerLine(state, row.decline_reason, row.employer_state_at ? sinceLabel(row.employer_state_at) : null)}
            </Typography>
            {state !== "declined" && (
                <Typography variant="caption" color="secondary">{PRESENTATION_STATE_HINT[state]}</Typography>
            )}
            {row.note_for_employer && (
                <Typography variant="body2" color="secondary" className="whitespace-pre-wrap break-words line-clamp-4">
                    Les dijimos: «{row.note_for_employer.trim()}»
                </Typography>
            )}
            <div className="flex gap-2 items-center flex-wrap">
                {!confirming ? (
                    <Button size="small" variant="text" disabled={working} onClick={() => setConfirming(true)}>
                        Retirar la presentación
                    </Button>
                ) : (
                    <>
                        <Typography variant="body2">
                            {companyName} dejará de ver la ficha de {talentName}
                            {state === "new" ? "." : ", y se pierde lo que respondieron."}
                        </Typography>
                        <Button size="small" variant="outlined" disabled={working} onClick={() => void withdraw()}>
                            {working ? "Retirando…" : "Sí, retirarla"}
                        </Button>
                        <Button size="small" variant="text" disabled={working} onClick={() => setConfirming(false)}>
                            No
                        </Button>
                    </>
                )}
                {error && <Typography variant="body2" color="error">{error}</Typography>}
            </div>
        </Card>
    );
}

/**
 * How many accounts each company has, over *every* membership row.
 *
 * This was one read of 500 rows, unordered, across all companies — so once the
 * table passed 500 the picker silently lost whichever employers happened to
 * fall past the cut, and an employer the team wanted to present to was simply
 * not in the list. The data plane refuses a limit above its ceiling rather than
 * clamping it (a 400 past 1000), so "all of them" is a walk in pages, ordered by
 * id so the pages neither overlap nor skip. Two stop conditions, as in
 * `ApplicantsPanel`: a short page is the end, and the reported total catches a
 * server that ignores `offset`. A walk that does not end is an error, shown as
 * one, rather than a list that looks complete.
 */
const MEMBER_PAGE = 500;
const MAX_MEMBER_PAGES = 40;

async function countMembersByCompany(data: any): Promise<Map<string, number>> {
    const counts = new Map<string, number>();
    let seen = 0;
    for (let pageNo = 0; ; pageNo++) {
        if (pageNo >= MAX_MEMBER_PAGES) throw new Error("company-members did not end");
        const res = await data.collection("company-members").find({
            orderBy: ["id", "asc"],
            limit: MEMBER_PAGE,
            offset: pageNo * MEMBER_PAGE
        });
        const page: any[] = res?.data ?? [];
        for (const m of page) {
            const id = entityIdOf(m.company_id);
            if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
        }
        seen += page.length;
        if (page.length < MEMBER_PAGE) break;
        const total = typeof res?.meta?.total === "number" ? res.meta.total : null;
        if (total !== null && seen >= total) break;
    }
    return counts;
}

/**
 * The companies with these ids, in chunks: an `in` over a few hundred ids is a
 * long query string, and each chunk's limit is its own length so none is cut.
 */
const COMPANY_CHUNK = 100;

async function fetchCompaniesById(data: any, ids: string[]): Promise<any[]> {
    const out: any[] = [];
    for (let i = 0; i < ids.length; i += COMPANY_CHUNK) {
        const chunk = ids.slice(i, i + COMPANY_CHUNK);
        const res = await data.collection("companies").find({ where: { id: ["in", chunk] }, limit: chunk.length });
        out.push(...((res?.data ?? []) as any[]));
    }
    return out;
}

/**
 * The confirmation, and the only place a presentation is written.
 *
 * Either end may be fixed by the caller: the company (its cover, the composer)
 * or the candidates (the candidate's cover). Whatever is not fixed is chosen
 * here, by a person, from a plain list.
 */
export function PresentPanel({
    company,
    candidates,
    onPresented,
    onCancel
}: {
    /** Fixed by the caller, or `null` to pick one here. */
    company: { id: string; name: string } | null;
    candidates: { id: string; name: string }[];
    /** Exactly who went in — a candidate skipped as already presented is not in it. */
    onPresented: (presented: { id: string; name: string }[]) => void | Promise<void>;
    onCancel: () => void;
}) {
    const client = useRebaseClient<RebaseClient>();
    const data = (client as any)?.data;

    const [picked, setPicked] = useState<{ id: string; name: string } | null>(company);
    useEffect(() => { setPicked(company); }, [company?.id]);

    // ── The employers a candidate can be presented to ──
    // Every company with at least one account, and only those. A presentation
    // is read in the employer's dashboard; a company with no members has no
    // dashboard, and presenting to one would open the candidate's row to
    // whoever claims that company next.
    const [options, setOptions] = useState<{ id: string; name: string; members: number }[] | null>(null);
    const [optionsFailed, setOptionsFailed] = useState(false);
    useEffect(() => {
        if (company || !data) return;
        let live = true;
        (async () => {
            try {
                const counts = await countMembersByCompany(data);
                const companies = await fetchCompaniesById(data, [...counts.keys()]);
                if (!live) return;
                setOptions(companies
                    .map(c => ({ id: String(c.id), name: (c.name ?? "").trim() || "(empresa sin nombre)", members: counts.get(String(c.id)) ?? 0 }))
                    // Name, then id: two companies can share a name (or both be
                    // "(empresa sin nombre)"), and the list must not reshuffle
                    // between two openings of the same dialog.
                    .sort((a, b) => a.name.localeCompare(b.name, "es") || a.id.localeCompare(b.id)));
            } catch {
                if (live) setOptionsFailed(true);
            }
        })();
        return () => { live = false; };
    }, [company, data]);

    // ── What we know about the chosen company ──
    const [context, setContext] = useState<CompanyContext | null>(null);
    const [contextFailed, setContextFailed] = useState(false);
    const [existing, setExisting] = useState<PresentationRow[]>([]);
    const candidateKey = candidates.map(c => c.id).join(",");
    useEffect(() => {
        setContext(null);
        setContextFailed(false);
        setExisting([]);
        if (!picked || !data) return;
        let live = true;
        (async () => {
            try {
                const [members, jobs, already] = await Promise.all([
                    data.collection("company-members").find({ where: { company_id: ["==", picked.id] }, limit: 50 }),
                    data.collection("jobs").find({ where: { company_id: ["==", picked.id] }, orderBy: ["created_at", "desc"], limit: 50 }),
                    candidates.length
                        ? data.collection("presented-candidates").find({
                            where: { company_id: ["==", picked.id], talent_id: ["in", candidates.map(c => c.id)] },
                            limit: 200
                        })
                        : Promise.resolve({ data: [] })
                ]);
                if (!live) return;
                setContext({
                    members: (members?.data ?? []) as CompanyContext["members"],
                    jobs: ((jobs?.data ?? []) as CompanyContext["jobs"]).filter(j => j.status !== "closed")
                });
                setExisting((already?.data ?? []) as PresentationRow[]);
            } catch {
                if (live) setContextFailed(true);
            }
        })();
        return () => { live = false; };
    }, [picked?.id, data, candidateKey]);

    const [jobId, setJobId] = useState("");
    useEffect(() => { setJobId(""); }, [picked?.id]);
    const [notes, setNotes] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState<{ count: number; skipped: string[] } | null>(null);
    useEffect(() => { setDone(null); setError(""); }, [candidateKey, picked?.id]);

    /** Already presented to this company for this vacancy (or generally, when none is chosen). */
    const alreadyPresented = useMemo(() => {
        const out = new Map<string, PresentationRow>();
        for (const row of existing) {
            const tid = entityIdOf(row.talent_id);
            if (tid && (entityIdOf(row.job_id) ?? "") === jobId) out.set(tid, row);
        }
        return out;
    }, [existing, jobId]);

    const toPresent = candidates.filter(c => !alreadyPresented.has(c.id));
    const problems = toPresent.map(c => noteProblem(notes[c.id])).filter(Boolean);
    const members = context?.members ?? [];
    const noAccount = context !== null && members.length === 0;
    const ready = Boolean(picked) && context !== null && !noAccount && toPresent.length > 0 && problems.length === 0;

    const present = async () => {
        if (!data || !picked || !ready) return;
        setSaving(true);
        setError("");
        const skipped: string[] = [];
        const presented: { id: string; name: string }[] = [];
        // One row each, in order, so a failure names the person it failed on
        // rather than leaving the operator to guess which of three went in.
        for (const candidate of toPresent) {
            try {
                await data.collection("presented-candidates").create({
                    company_id: picked.id,
                    talent_id: candidate.id,
                    job_id: jobId || null,
                    note_for_employer: (notes[candidate.id] ?? "").trim()
                });
                presented.push(candidate);
            } catch (err: any) {
                const message = String(err?.message ?? "");
                skipped.push(/once_per_vacancy|duplicate|unique/i.test(message)
                    ? `${candidate.name} (ya estaba presentada)`
                    : `${candidate.name} (${message || "error desconocido"})`);
            }
        }
        setSaving(false);
        setDone({ count: presented.length, skipped });
        if (presented.length > 0) await onPresented(presented);
        else setError("No se presentó a nadie.");
    };

    const writeTo = members.find(m => (m.contact_email ?? "").trim());

    if (done && done.count > 0) {
        return (
            <Card className={cls("p-4 flex flex-col gap-2", defaultBorderMixin)}>
                <Typography variant="body2" className="font-medium">
                    {done.count === 1 ? "1 persona presentada" : `${done.count} personas presentadas`} a {picked?.name}.
                </Typography>
                <Typography variant="body2" color="secondary">
                    Lo verán en su panel la próxima vez que entren. No les llega ningún aviso automático.
                </Typography>
                {done.skipped.length > 0 && (
                    <Alert color="warning" size="small">No se presentó a: {done.skipped.join(", ")}.</Alert>
                )}
                <div className="flex gap-3 items-center flex-wrap">
                    {writeTo?.contact_email && (
                        <a
                            className="text-sm text-primary hover:underline"
                            href={`/correo?para=${encodeURIComponent(writeTo.contact_email)}&asunto=${encodeURIComponent("Candidatos para " + (picked?.name ?? ""))}`}
                        >
                            Escribir a {writeTo.contact_name || writeTo.contact_email} para avisarle →
                        </a>
                    )}
                    <Button size="small" variant="text" onClick={onCancel}>Cerrar</Button>
                </div>
            </Card>
        );
    }

    return (
        <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
            {!company && (
                <div className="flex flex-col gap-1">
                    <Typography variant="body2" color="secondary">A qué empresa</Typography>
                    {optionsFailed && <Typography variant="body2" color="error">No se pudo leer la lista de empresas con cuenta.</Typography>}
                    {options === null && !optionsFailed && <Typography variant="body2" color="secondary">Leyendo empresas…</Typography>}
                    {options?.length === 0 && (
                        <Typography variant="body2" color="secondary">
                            Ninguna empresa tiene todavía a alguien con cuenta, así que no hay panel donde presentar a nadie.
                        </Typography>
                    )}
                    {options && options.length > 0 && (
                        <select
                            aria-label="Empresa a la que presentar"
                            className="text-sm rounded-md border px-2 py-1.5 bg-transparent"
                            value={picked?.id ?? ""}
                            disabled={saving}
                            onChange={e => {
                                const option = options.find(o => o.id === e.target.value);
                                setPicked(option ? { id: option.id, name: option.name } : null);
                            }}
                        >
                            <option value="">Elegir empresa…</option>
                            {options.map(o => (
                                <option key={o.id} value={o.id}>
                                    {o.name} · {o.members} {plural(o.members, "persona con cuenta", "personas con cuenta")}
                                </option>
                            ))}
                        </select>
                    )}
                    <Typography variant="caption" color="secondary">
                        Sólo aparecen las empresas con alguien con cuenta: es en su panel donde lo verán.
                    </Typography>
                </div>
            )}

            {picked && contextFailed && (
                <Alert color="error" size="small">
                    No se pudo leer quién tiene cuenta en {picked.name} ni sus vacantes. No presentes hasta que se pueda comprobar.
                </Alert>
            )}
            {picked && context === null && !contextFailed && (
                <Typography variant="body2" color="secondary">Comprobando {picked.name}…</Typography>
            )}

            {picked && noAccount && (
                <Typography variant="body2">
                    Nadie tiene cuenta en {picked.name}, así que no hay panel donde verlo — y presentarla abriría
                    su ficha a quien reclame la empresa después. Nómbrala en un correo desde «Empresas que contratan».
                </Typography>
            )}

            {picked && context && !noAccount && (
                <>
                    <div className="flex flex-col gap-1">
                        <Typography variant="body2" color="secondary">Para qué vacante</Typography>
                        <select
                            aria-label="Vacante"
                            className="text-sm rounded-md border px-2 py-1.5 bg-transparent"
                            value={jobId}
                            disabled={saving}
                            onChange={e => setJobId(e.target.value)}
                        >
                            <option value="">Ninguna en concreto — presentación general</option>
                            {context.jobs.map(j => (
                                <option key={j.id} value={j.id}>{(j.title ?? "").trim() || "(vacante sin título)"}</option>
                            ))}
                        </select>
                    </div>

                    {candidates.map(candidate => {
                        const previous = alreadyPresented.get(candidate.id);
                        if (previous) {
                            return (
                                <Typography key={candidate.id} variant="body2" color="secondary">
                                    {candidate.name}: ya se la presentamos {jobId ? "para esta vacante" : "en general"} {sinceLabel(previous.presented_at)} —
                                    {" "}{PRESENTATION_STATE_LABEL[presentationState(previous.employer_state)].toLowerCase()}.
                                </Typography>
                            );
                        }
                        const value = notes[candidate.id] ?? "";
                        return (
                            <TextField
                                key={candidate.id}
                                size="small"
                                multiline
                                minRows={2}
                                label={`Qué le decimos a ${picked.name} sobre ${candidate.name}`}
                                value={value}
                                disabled={saving}
                                error={value.length > NOTE_MAX}
                                onChange={(e: any) => setNotes(prev => ({ ...prev, [candidate.id]: e.target.value }))}
                            />
                        );
                    })}

                    {/* The consequence, before the button. Written against what
                        the API lets them read, not what their card happens to
                        show: RLS opens the whole row. */}
                    <Typography variant="body2">
                        {picked.name} podrá leer la ficha completa de {toPresent.length === 1 ? "esta persona" : `estas ${toPresent.length} personas`} —
                        email, teléfono, LinkedIn y todo el cuestionario, expectativa salarial incluida — y tu nota,
                        además de la foto y el CV que haya subido la propia persona (un CV importado por el equipo, no).
                        Lo verán las {members.length} {plural(members.length, "persona", "personas")} con cuenta en {picked.name}.
                    </Typography>
                    <Typography variant="caption" color="secondary">
                        La persona candidata no ve tu nota; sí ve en su panel que hemos presentado su perfil a {picked.name}.
                    </Typography>
                </>
            )}

            {error && <Alert color="error" size="small">{error}</Alert>}
            {done && done.skipped.length > 0 && (
                <Alert color="warning" size="small">No se presentó a: {done.skipped.join(", ")}.</Alert>
            )}

            <div className="flex gap-2 items-center flex-wrap">
                <Button size="small" disabled={!ready || saving} onClick={() => void present()}>
                    {saving ? "Presentando…" : `Presentar a ${picked?.name ?? "…"}`}
                </Button>
                <Button size="small" variant="text" disabled={saving} onClick={onCancel}>Cancelar</Button>
                {picked && context && !noAccount && toPresent.length > 0 && problems.length > 0 && (
                    <Typography variant="body2" color="secondary">
                        {problems.length === 1 ? "Falta una nota." : `Faltan ${problems.length} notas.`}
                    </Typography>
                )}
                {picked && context && !noAccount && toPresent.length === 0 && (
                    <Typography variant="body2" color="secondary">Ya están todas presentadas.</Typography>
                )}
            </div>
        </Card>
    );
}

/**
 * "Candidatos presentados" on a company's cover: who, what they did with it,
 * and the door to present somebody else.
 */
export function CompanyPresentations({ companyId, companyName }: { companyId: string; companyName: string }) {
    const client = useRebaseClient<RebaseClient>();
    const { rows, failed, names, reload } = usePresentations({ company_id: companyId });
    const [picking, setPicking] = useState(false);
    const [shortlist, setShortlist] = useState<ShortlistCandidate[]>([]);

    useEffect(() => { setPicking(false); setShortlist([]); }, [companyId]);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <Typography variant="subtitle2" component="h3" className="grow">
                    Candidatos presentados{rows ? ` (${rows.length})` : ""}
                </Typography>
                <Button size="small" variant={picking ? "filled" : "outlined"} onClick={() => setPicking(v => !v)}>
                    {picking ? "Dejar de elegir" : "Presentar candidatos"}
                </Button>
            </div>
            {rows && rows.length > 0 && (
                <Typography variant="body2" color="secondary">{presentationTally(rows)}</Typography>
            )}

            {picking && (
                <div className="flex flex-col gap-2">
                    <CandidateShortlist client={client} selected={shortlist} onChange={setShortlist} purpose="presentation" />
                    {shortlist.length > 0 && (
                        <PresentPanel
                            company={{ id: companyId, name: companyName }}
                            candidates={shortlist.map(c => ({ id: c.id, name: shortlistLabel(c) }))}
                            // The shortlist stays until "Cerrar": clearing it
                            // here would unmount the panel and, with it, the
                            // confirmation and the link to tell the employer.
                            onPresented={() => reload()}
                            onCancel={() => { setPicking(false); setShortlist([]); }}
                        />
                    )}
                </div>
            )}

            {failed && (
                <Typography variant="body2" color="error">
                    No se pudo leer a quién le hemos presentado. No es que no haya nadie: la consulta falló.
                </Typography>
            )}
            {rows?.length === 0 && (
                <Typography variant="body2" color="secondary">
                    Todavía no le hemos presentado a nadie.
                </Typography>
            )}
            {rows?.map(row => (
                <PresentationCard key={row.id} row={row} perspective="company" names={names} onWithdrawn={() => void reload()} />
            ))}
        </div>
    );
}

/**
 * "Presentaciones a empresas" on a candidate's cover.
 */
export function TalentPresentations({ talentId, talentName }: { talentId: string; talentName: string }) {
    const { rows, failed, names, reload } = usePresentations({ talent_id: talentId });
    const [presenting, setPresenting] = useState(false);

    useEffect(() => { setPresenting(false); }, [talentId]);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <Typography variant="subtitle2" component="h3" className="grow">
                    Presentaciones a empresas{rows ? ` (${rows.length})` : ""}
                </Typography>
                {!presenting && (
                    <Button size="small" variant="outlined" onClick={() => setPresenting(true)}>
                        Presentar a una empresa…
                    </Button>
                )}
            </div>

            {presenting && (
                <PresentPanel
                    company={null}
                    candidates={[{ id: talentId, name: talentName }]}
                    onPresented={() => reload()}
                    onCancel={() => setPresenting(false)}
                />
            )}

            {failed && (
                <Typography variant="body2" color="error">
                    No se pudieron leer sus presentaciones. No es que no haya ninguna: la consulta falló.
                </Typography>
            )}
            {rows?.length === 0 && !presenting && (
                <Typography variant="body2" color="secondary">
                    Todavía no hemos presentado a esta persona a ninguna empresa.
                </Typography>
            )}
            {rows?.map(row => (
                <PresentationCard key={row.id} row={row} perspective="talent" names={names} onWithdrawn={() => void reload()} />
            ))}
        </div>
    );
}
